import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, ArrowRight, Send } from "lucide-react";
import {
  Card,
  ErrorMessage,
  PageHeader,
  VButton,
  VStepper,
  type VStepperStep,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useCreateRequest } from "@/domains/requests/api/mutations";
import type {
  CreateRequestInput,
  DeploymentLocation,
  DeploymentPeriod,
  ExtraInfo,
  RequestMotivation,
  RequestedAssetLine,
  RequesterDetails,
} from "@/domains/requests/api/types";
import {
  EMPTY_ASSETS,
  EMPTY_EXTRA_INFO,
  EMPTY_LOCATION,
  EMPTY_MOTIVATION,
  EMPTY_PERIOD,
  WIZARD_STEPS,
} from "@/domains/requests/components/requestWizard/wizardState";
import { StepRequester } from "@/domains/requests/components/requestWizard/stepRequester";
import { StepAssets } from "@/domains/requests/components/requestWizard/stepAssets";
import { StepPeriod } from "@/domains/requests/components/requestWizard/stepPeriod";
import { StepLocation } from "@/domains/requests/components/requestWizard/stepLocation";
import { StepExtraInfo } from "@/domains/requests/components/requestWizard/stepExtraInfo";
import { StepMotivation } from "@/domains/requests/components/requestWizard/stepMotivation";
import { StepSubmit } from "@/domains/requests/components/requestWizard/stepSubmit";

const STEPPER_STEPS: ReadonlyArray<VStepperStep> = WIZARD_STEPS.map((s) => ({ label: s.label }));

export function RequestWizard(): React.JSX.Element {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const createRequest = useCreateRequest();

  const initialRequester = useMemo<RequesterDetails>(
    () => ({
      lastName: currentUser.lastName,
      firstName: currentUser.firstName,
      email: currentUser.email,
      phone: currentUser.phone,
      service: currentUser.service,
      serviceContactDetails: `${currentUser.email} — ${currentUser.phone}`,
    }),
    [currentUser],
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [title, setTitle] = useState("");
  const [qualityConfirmed, setQualityConfirmed] = useState(false);
  const [requester, setRequester] = useState<RequesterDetails>(initialRequester);

  useEffect(() => {
    setRequester(initialRequester);
  }, [initialRequester]);

  const [assets, setAssets] = useState<ReadonlyArray<RequestedAssetLine>>(EMPTY_ASSETS);
  const [period, setPeriod] = useState<DeploymentPeriod>(EMPTY_PERIOD);
  const [location, setLocation] = useState<DeploymentLocation>(EMPTY_LOCATION);
  const [extraInfo, setExtraInfo] = useState<ExtraInfo>(EMPTY_EXTRA_INFO);
  const [motivation, setMotivation] = useState<RequestMotivation>(EMPTY_MOTIVATION);

  const draft: CreateRequestInput = useMemo(
    () => ({
      title,
      requesterId: currentUser.id,
      requester,
      assets,
      period,
      location,
      extraInfo,
      motivation,
      qualityConfirmed,
    }),
    [
      currentUser.id,
      title,
      requester,
      assets,
      period,
      location,
      extraInfo,
      motivation,
      qualityConfirmed,
    ],
  );

  const canGoNext = stepIndex < WIZARD_STEPS.length - 1;
  const canSubmit = title.trim().length > 0 && qualityConfirmed;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    createRequest.mutate(draft, {
      onSuccess: (created) => navigate(`/requests/${created.id}`),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aanvragen van een mobiele camera"
        subtitle="Doorloop de 6 stappen om je aanvraag in te dienen. Velden met een * zijn verplicht."
      />

      <Card padding="md">
        <VStepper
          steps={STEPPER_STEPS}
          currentStep={stepIndex}
          onStepClick={(idx) => setStepIndex(idx)}
        />
      </Card>

      {stepIndex === 0 ? (
        <StepRequester value={requester} onChange={setRequester} />
      ) : stepIndex === 1 ? (
        <StepAssets value={assets} onChange={setAssets} />
      ) : stepIndex === 2 ? (
        <StepPeriod value={period} onChange={setPeriod} />
      ) : stepIndex === 3 ? (
        <StepLocation value={location} onChange={setLocation} />
      ) : stepIndex === 4 ? (
        <StepExtraInfo value={extraInfo} onChange={setExtraInfo} />
      ) : stepIndex === 5 ? (
        <StepMotivation value={motivation} onChange={setMotivation} />
      ) : (
        <StepSubmit
          title={title}
          qualityConfirmed={qualityConfirmed}
          draft={draft}
          onChangeTitle={setTitle}
          onChangeQualityConfirmed={setQualityConfirmed}
        />
      )}

      {createRequest.isError ? (
        <ErrorMessage message="Indienen mislukte. Probeer opnieuw." />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <VButton
          variant="outline"
          disabled={stepIndex === 0}
          onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
        >
          <ArrowLeft className="size-4" aria-hidden />
          Terug
        </VButton>
        {canGoNext ? (
          <VButton onClick={() => setStepIndex((i) => i + 1)}>
            Volgende
            <ArrowRight className="size-4" aria-hidden />
          </VButton>
        ) : (
          <VButton onClick={handleSubmit} disabled={!canSubmit || createRequest.isPending}>
            <Send className="size-4" aria-hidden />
            {createRequest.isPending ? "Indienen…" : "Verzenden"}
          </VButton>
        )}
      </div>
    </div>
  );
}
